import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { uploadPhoto } from "@/lib/reviews";

export const PhotoPicker = ({ value, onChange, max = 6, testPrefix, onBusy }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(0);

  const pick = async (e) => {
    const files = [...e.target.files].slice(0, max - value.length);
    e.target.value = "";
    if (!files.length) return;
    setBusy(files.length);
    onBusy?.(true);
    let list = value;
    for (const f of files) {
      try {
        const id = await uploadPhoto(f);
        list = [...list, { id, preview: URL.createObjectURL(f) }];
        onChange(list);
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err)}`);
      }
      setBusy((b) => b - 1);
    }
    onBusy?.(false);
  };

  return (
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid={`${testPrefix}-photos`}>
      {value.map((p, i) => (
        <div key={p.id} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100" data-testid={`${testPrefix}-photo-${i}`}>
          <img src={p.preview} alt={`Upload ${i + 1}`} className="h-full w-full object-cover" />
          <button type="button" onClick={() => onChange(value.filter((x) => x.id !== p.id))} aria-label="Remove photo" data-testid={`${testPrefix}-photo-remove-${i}`}
            className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-navy/80 text-white hover:bg-rose-600 transition-colors">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      {Array.from({ length: busy }).map((_, i) => (
        <div key={`b${i}`} className="aspect-square rounded-xl bg-sky-50 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-ocean" /></div>
      ))}
      {value.length + busy < max && (
        <button type="button" onClick={() => input.current?.click()} data-testid={`${testPrefix}-photo-add`}
          className="aspect-square rounded-xl border-2 border-dashed border-sky-200 text-ocean grid place-items-center hover:border-ocean hover:bg-sky-50 transition-colors">
          <span className="flex flex-col items-center gap-1 text-xs font-semibold"><ImagePlus className="h-5 w-5" />Add photo</span>
        </button>
      )}
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={pick} data-testid={`${testPrefix}-photo-input`} />
    </div>
  );
};
